import { useEffect, useRef, useState } from "react";
import { isHttpClientError } from "../../../lib/http/errors";
import { getRailwayTransfers } from "../repo/itineraryRailwayTransferRepo";
import type { RailwayQuery, RailwayTransferOption, RailwayTransferSearchResult } from "../types";
import { railwayTransferSeat, railwayTransferSelectionId, railwayTransferUnavailableReason } from "../service/itineraryRailwayTransferQueryService";

export type RailwayTransferFetcher = typeof getRailwayTransfers;
export type RailwayTransferSelection = {
  id: string; option: RailwayTransferOption; result: RailwayTransferSearchResult;
  queriedAt: string; seatClasses: Record<string, string>;
};
export type RailwayTransferFare = { seatClass: string; price: number; availability?: string | null };
type State = { key: string; loading: boolean; error: string; result: RailwayTransferSearchResult | null;
  queriedAt: string; selectedId: string; seatClasses: Record<string, string>; hasQueried: boolean;
  pendingFares: Record<string, Record<number, RailwayTransferFare>> };
const empty: State = { key: "", loading: false, error: "", result: null, queriedAt: "", selectedId: "", seatClasses: {}, hasQueried: false, pendingFares: {} };

/** 请求归属模式、路线、端点和日期；取消与迟到响应都不能覆盖新上下文。 */
export function useItineraryRailwayTransfers({ active, contextKey, query, initialSelections = [], fetcher = getRailwayTransfers }: {
  active: boolean; contextKey: string; query: RailwayQuery; fetcher?: RailwayTransferFetcher;
  initialSelections?: RailwayTransferSelection[];
}) {
  const key = JSON.stringify([active, contextKey, query.fromStationIds, query.toStationIds, query.date, query.queryScope]);
  const selectionKey = JSON.stringify([contextKey, query.fromStationIds, query.toStationIds, query.queryScope]);
  const initial = useRef(initialSelections); initial.current = initialSelections;
  const [chosen, setChosen] = useState<{ key: string; selections: RailwayTransferSelection[] }>({ key: selectionKey, selections: initialSelections });
  const selections = chosen.key === selectionKey ? chosen.selections : [];
  const currentKey = useRef(key);
  currentKey.current = key;
  const abort = useRef<AbortController | null>(null);
  const generation = useRef(0);
  const [state, setState] = useState<State>(empty);
  const cancel = (preserveSelections = false) => {
    abort.current?.abort(); abort.current = null; generation.current += 1;
    setState(empty);
    if (!preserveSelections) setChosen({ key: "", selections: [] });
  };
  useEffect(() => {
    if (active) setChosen((value) => value.key === selectionKey ? value : { key: selectionKey, selections: structuredClone(initial.current) });
  }, [selectionKey, active]);
  useEffect(() => {
    abort.current?.abort(); abort.current = null; generation.current += 1;
    setState(empty);
    return () => { abort.current?.abort(); generation.current += 1; };
  }, [key]);
  const current = active && state.key === key ? state : empty;
  const pendingFares = { ...(current.pendingFares ?? {}) };
  const selectionId = (option: RailwayTransferOption) => railwayTransferSelectionId(option, current.seatClasses);
  const addSelection = (optionId: string, replace: boolean) => {
    const option = current.result?.transfers.find((item) => item.optionId === optionId);
    if (!option || !current.result || railwayTransferUnavailableReason(option)) return;
    const item: RailwayTransferSelection = { id: selectionId(option), option: structuredClone(option),
      result: { ...structuredClone(current.result), transfers: [structuredClone(option)] },
      queriedAt: current.queriedAt, seatClasses: Object.fromEntries(option.legs.map((leg) => [leg.optionId, railwayTransferSeat(leg, current.seatClasses)?.seatClass ?? ""])) };
    setChosen((value) => {
      const previous = value.key === selectionKey ? value.selections : [];
      return { key: selectionKey, selections: replace ? [item] : previous.some((entry) => entry.id === item.id)
        ? previous.filter((entry) => entry.id !== item.id) : [item, ...previous] };
    });
  };
  const chosenPair = (option: RailwayTransferOption, fares: Record<number, RailwayTransferFare>): RailwayTransferSelection | null => {
    if (!fares[0] || !fares[1] || !current.result) return null;
    const selectedOption = { ...structuredClone(option), legs: option.legs.map((leg, index) => ({ ...structuredClone(leg), seats: [fares[index]] })) };
    const seatClasses = Object.fromEntries(selectedOption.legs.map((leg, index) => [leg.optionId, fares[index].seatClass]));
    return { id: railwayTransferSelectionId(selectedOption, seatClasses), option: selectedOption,
      result: { ...structuredClone(current.result), transfers: [selectedOption] }, queriedAt: current.queriedAt, seatClasses };
  };
  const chooseFare = (optionId: string, legIndex: number, fare: RailwayTransferFare) => {
    const option = current.result?.transfers.find((item) => item.optionId === optionId);
    if (!option || !option.legs[legIndex] || railwayTransferUnavailableReason(option)
      || !Number.isFinite(fare.price) || fare.price < 0) return;
    const pending = pendingFares[optionId] ?? {};
    const nextFares = { ...pending };
    const removing = pending[legIndex]?.seatClass === fare.seatClass;
    if (removing) delete nextFares[legIndex]; else nextFares[legIndex] = fare;
    const previous = chosenPair(option, pending); const next = chosenPair(option, nextFares);
    setState((value) => value.key === key ? { ...value, pendingFares: { ...value.pendingFares, [optionId]: nextFares } } : value);
    setChosen((value) => {
      const items = value.key === selectionKey ? value.selections : [];
      return { key: selectionKey, selections: removing && previous ? items.filter((item) => item.id !== previous.id)
        : next ? [items.find((item) => item.id === next.id) ?? next, ...items.filter((item) => item.id !== next.id)] : items };
    });
  };
  const runQuery = async () => {
    if (!active) return;
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    const version = ++generation.current;
    const owns = () => currentKey.current === key && generation.current === version && !controller.signal.aborted;
    setState({ ...empty, key, loading: true, hasQueried: true });
    try {
      const result = await fetcher(query, controller.signal);
      if (owns()) setState({ ...empty, key, result, queriedAt: new Date().toISOString(), hasQueried: true });
    } catch (reason) {
      if (owns()) setState({ ...empty, key, hasQueried: true, error: transferError(reason) });
    }
  };
  return {
    ...current, pendingFares, queryKey: key, runQuery, cancel,
    selections, selected: selections[0]?.option ?? null, selectedId: selections[0]?.option.optionId ?? "",
    chooseFare, isFareSelected: (optionId: string, legIndex: number, seatClass: string) => pendingFares[optionId]?.[legIndex]?.seatClass === seatClass,
    select: (optionId: string) => addSelection(optionId, true),
    toggleSelection: (optionId: string) => addSelection(optionId, false),
    isSelected: (optionId: string) => {
      const option = current.result?.transfers.find((item) => item.optionId === optionId);
      return Boolean(option && selections.some((item) => item.id === selectionId(option)));
    },
    removeSelection: (id: string) => {
      setChosen((value) => ({ ...value, selections: value.selections.filter((item) => item.id !== id) }));
      setState((value) => ({ ...value, pendingFares: Object.fromEntries(Object.entries(value.pendingFares ?? {}).filter(([optionId, fares]) => {
        const option = current.result?.transfers.find((item) => item.optionId === optionId);
        return !option || chosenPair(option, fares)?.id !== id;
      })) }));
    },
    moveSelection: (id: string, targetId: string) => setChosen((value) => {
      const source = value.selections.findIndex((item) => item.id === id);
      const target = value.selections.findIndex((item) => item.id === targetId);
      if (source < 0 || target < 0 || source === target) return value;
      const ordered = [...value.selections]; const [item] = ordered.splice(source, 1); ordered.splice(target, 0, item);
      return { ...value, selections: ordered };
    }),
    selectSeat: (optionId: string, seatClass: string) => setState((value) => value.key === key
      ? { ...value, seatClasses: { ...value.seatClasses, [optionId]: seatClass } } : value),
  };
}

/** 中转空结果与上游故障使用不同提示，避免复用旧直达候选。 */
function transferError(reason: unknown) {
  if (isHttpClientError(reason)) {
    if (reason.code === "ITINERARY_TRANSPORT_EMPTY") return "当天未查到可用中转方案，请调整车站或日期后重试。";
    if (["ITINERARY_TRANSPORT_TIMEOUT", "ECONNABORTED", "ETIMEDOUT"].includes(reason.code ?? "")) return "中转查询超时，请稍后重试。";
    if (reason.code === "ITINERARY_TRANSPORT_INVALID" || reason.statusCode === 422) return "中转查询条件无效，请检查车站和日期。";
  }
  return "中转查询失败，请稍后重试。";
}
